import React, { useState, useMemo } from 'react';
import { 
  Organization, 
  UserProfile, 
  ClassRoom, 
  StudentConnection, 
  OrgStatus,
  OrgType,
  Role,
  OrgVerificationData
} from '../types';
import { Avatar } from './common';
import { OrgModulesModal } from './OrgModulesModal';
import { SystemQADiagnosticsModal } from './SystemQADiagnosticsModal';
import { SuperAdminReassignModal } from './SuperAdminReassignModal';
import { MODULE_DEFINITIONS } from '../lib/features';

interface SuperAdminProps {
  currentUser: UserProfile;
  organizations: Organization[];
  users: UserProfile[];
  classes: ClassRoom[];
  students: StudentConnection[];
  onApproveOrg: (orgId: string, adminId?: string) => Promise<void>;
  onRejectOrg: (orgId: string, reason: string, adminId?: string) => Promise<void>;
  onSuspendOrg: (orgId: string) => Promise<void>;
  onReactivateOrg: (orgId: string) => Promise<void>;
  onUpdateOrg: (orgId: string, data: Partial<Organization>) => Promise<void>;
  onUpdateUserRole: (userId: string, role: Role) => Promise<void>;
  onReassignOrgAdmin?: (params: {
    orgId: string;
    newAdminEmail: string;
    newAdminId?: string;
    newAdminName?: string;
    outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
    reason?: string;
    referenceTicket?: string;
    transferType?: 'self_serve' | 'super_admin_reassignment';
  }) => Promise<void>;
  onSwitchView?: (role: Role, orgId?: string) => void;
  onLogout: () => void;
}

export const SuperAdminManager: React.FC<SuperAdminProps> = ({
  currentUser,
  organizations,
  users,
  classes,
  students,
  onApproveOrg,
  onRejectOrg,
  onSuspendOrg,
  onReactivateOrg,
  onUpdateOrg,
  onUpdateUserRole,
  onReassignOrgAdmin,
  onSwitchView,
  onLogout
}) => {
  const [activeTab, setActiveTab] = useState<'organizations' | 'users' | 'analytics'>('organizations');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'active' | 'suspended' | 'rejected'>('pending');
  const [typeFilter, setTypeFilter] = useState<'all' | 'school' | 'coaching'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals / Actions
  const [selectedOrgForDossier, setSelectedOrgForDossier] = useState<Organization | null>(null);
  const [configuringModulesOrg, setConfiguringModulesOrg] = useState<Organization | null>(null);
  const [reassigningOrg, setReassigningOrg] = useState<Organization | null>(null);
  const [rejectingOrg, setRejectingOrg] = useState<Organization | null>(null);
  const [rejectionReason, setRejectionReason] = useState('Unverifiable institution details or incomplete credentials');
  const [customRejectionText, setCustomRejectionText] = useState('');
  const [previewDocUrl, setPreviewDocUrl] = useState<string | null>(null);
  const [showQAModal, setShowQAModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string | null>(null);

  // User management filters
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Counts & Stats
  const pendingOrgs = useMemo(() => organizations.filter(o => (o.status || 'pending') === 'pending'), [organizations]);
  const activeOrgs = useMemo(() => organizations.filter(o => o.status === 'active' || (!o.status && o.adminId)), [organizations]);
  const suspendedOrgs = useMemo(() => organizations.filter(o => o.status === 'suspended'), [organizations]);
  const rejectedOrgs = useMemo(() => organizations.filter(o => o.status === 'rejected'), [organizations]);

  const teacherUsers = useMemo(() => users.filter(u => u.role === 'teacher'), [users]);
  const parentUsers = useMemo(() => users.filter(u => u.role === 'parent'), [users]);
  const principalUsers = useMemo(() => users.filter(u => u.role === 'principal'), [users]);
  const superAdminUsers = useMemo(() => users.filter(u => u.role === 'super_admin'), [users]);

  // Filtered Orgs
  const filteredOrgs = useMemo(() => {
    return organizations.filter(org => {
      const orgStatus = org.status || 'pending';
      if (statusFilter !== 'all' && orgStatus !== statusFilter) return false;
      if (typeFilter !== 'all' && org.type !== typeFilter) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const adminUser = users.find(u => u.id === org.adminId);
        const nameMatch = org.name.toLowerCase().includes(q);
        const cityMatch = org.address?.city?.toLowerCase().includes(q);
        const stateMatch = org.address?.state?.toLowerCase().includes(q);
        const adminNameMatch = adminUser?.name.toLowerCase().includes(q);
        const adminEmailMatch = adminUser?.email.toLowerCase().includes(q);
        const phoneMatch = org.phone?.toLowerCase().includes(q);
        const affMatch = org.verification?.affiliationNumber?.toLowerCase().includes(q);

        if (!nameMatch && !cityMatch && !stateMatch && !adminNameMatch && !adminEmailMatch && !phoneMatch && !affMatch) {
          return false;
        }
      }
      return true;
    }).sort((a, b) => {
      // Sort pending first, then by date
      if ((a.status || 'pending') === 'pending' && (b.status || 'pending') !== 'pending') return -1;
      if ((a.status || 'pending') !== 'pending' && (b.status || 'pending') === 'pending') return 1;
      return (b.createdAt ? new Date(b.createdAt).getTime() : 0) - (a.createdAt ? new Date(a.createdAt).getTime() : 0);
    });
  }, [organizations, statusFilter, typeFilter, searchQuery, users]);

  // Filtered Users
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      if (userRoleFilter !== 'all' && u.role !== userRoleFilter) return false;
      if (userSearchQuery.trim()) {
        const q = userSearchQuery.toLowerCase();
        const nameMatch = u.name.toLowerCase().includes(q);
        const emailMatch = u.email.toLowerCase().includes(q);
        const phoneMatch = u.mobileNumber?.toLowerCase().includes(q);
        const orgMatch = organizations.find(o => o.id === u.orgId)?.name.toLowerCase().includes(q);
        if (!nameMatch && !emailMatch && !phoneMatch && !orgMatch) return false;
      }
      return true;
    });
  }, [users, userRoleFilter, userSearchQuery, organizations]);

  const showToast = (msg: string) => {
    setActionSuccessMsg(msg);
    setTimeout(() => setActionSuccessMsg(null), 4500);
  };

  const handleApprove = async (org: Organization) => {
    setIsProcessing(org.id);
    try {
      await onApproveOrg(org.id, org.adminId);
      showToast(`Successfully verified & activated "${org.name}". Principal has been unlocked.`);
      if (selectedOrgForDossier?.id === org.id) setSelectedOrgForDossier(null);
    } catch (err: any) {
      alert(`Approval error: ${err.message}`);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectingOrg) return;
    const finalReason = customRejectionText.trim() || rejectionReason;
    setIsProcessing(rejectingOrg.id);
    try {
      await onRejectOrg(rejectingOrg.id, finalReason, rejectingOrg.adminId);
      showToast(`Institution "${rejectingOrg.name}" marked as Rejected.`);
      setRejectingOrg(null);
      setCustomRejectionText('');
      if (selectedOrgForDossier?.id === rejectingOrg.id) setSelectedOrgForDossier(null);
    } catch (err: any) {
      alert(`Rejection error: ${err.message}`);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleConfirmReassignAdmin = async (params: {
    orgId: string;
    newAdminEmail: string;
    newAdminId?: string;
    newAdminName?: string;
    reasonCategory: string;
    referenceTicket: string;
    outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
  }) => {
    if (!onReassignOrgAdmin) {
      alert("Administrator reassignment handler is not configured.");
      return;
    }
    await onReassignOrgAdmin({
      orgId: params.orgId,
      newAdminEmail: params.newAdminEmail,
      newAdminId: params.newAdminId,
      newAdminName: params.newAdminName,
      outgoingAdminRole: params.outgoingAdminRole,
      reason: params.reasonCategory,
      referenceTicket: params.referenceTicket,
      transferType: 'super_admin_reassignment'
    });
    showToast(`Successfully reassigned institution administrator.`);
    setReassigningOrg(null);
    if (selectedOrgForDossier?.id === params.orgId) {
      setSelectedOrgForDossier(null);
    }
  };

  const handleSuspend = async (org: Organization) => {
    if (!confirm(`Are you sure you want to suspend "${org.name}"? Teachers and parents in this org will not be able to access data.`)) return;
    setIsProcessing(org.id);
    try {
      await onSuspendOrg(org.id);
      showToast(`"${org.name}" has been suspended.`);
    } catch (err: any) {
      alert(`Suspend error: ${err.message}`);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleReactivate = async (org: Organization) => {
    setIsProcessing(org.id);
    try {
      await onReactivateOrg(org.id);
      showToast(`"${org.name}" has been reactivated.`);
    } catch (err: any) {
      alert(`Reactivation error: ${err.message}`);
    } finally {
      setIsProcessing(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Top Super Admin Navigation Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-amber-500 to-indigo-500 text-white flex items-center justify-center font-black shadow-inner">
              <span className="material-icons-round text-xl">admin_panel_settings</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-extrabold text-base tracking-tight text-white">EduGravity</h1>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold uppercase tracking-wider border border-amber-500/30">
                  Super Admin
                </span>
              </div>
              <p className="text-[11px] text-slate-400">Platform Verification &amp; Anti-Spam Control</p>
            </div>
          </div>

          {/* Center Tabs */}
          <div className="flex items-center bg-slate-800/90 p-1 rounded-xl border border-slate-700/60 text-xs font-semibold">
            <button
              onClick={() => setActiveTab('organizations')}
              className={`px-3.5 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${
                activeTab === 'organizations'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <span className="material-icons-round text-sm">verified_user</span>
              <span>Institutions</span>
              {pendingOrgs.length > 0 && (
                <span className="px-1.5 py-0.2 bg-amber-400 text-slate-950 font-extrabold rounded-full text-[10px] animate-pulse">
                  {pendingOrgs.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-3.5 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${
                activeTab === 'users'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <span className="material-icons-round text-sm">people</span>
              <span>Users</span>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-3.5 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${
                activeTab === 'analytics'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <span className="material-icons-round text-sm">bar_chart</span>
              <span>Platform Stats</span>
            </button>
          </div>

          {/* Right User & Logout */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowQAModal(true)}
              className="px-3 py-1.5 bg-emerald-950/70 border border-emerald-500/50 hover:bg-emerald-900/80 text-emerald-300 text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
              title="Run Automated QA & Health Diagnostics"
            >
              <span className="material-icons-round text-sm text-emerald-400">health_and_safety</span>
              <span className="hidden sm:inline">System QA</span>
            </button>

            {onSwitchView && (
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    const [role, orgId] = e.target.value.split(':');
                    onSwitchView(role as Role, orgId || undefined);
                  }
                }}
                defaultValue=""
                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="" disabled>Simulate View As...</option>
                {activeOrgs.map(org => (
                  <option key={`p:${org.id}`} value={`principal:${org.id}`}>
                    🏫 Principal ({org.name})
                  </option>
                ))}
                <option value="teacher:">👩‍🏫 Teacher View</option>
                <option value="parent:">👨‍👩‍👧 Parent View</option>
              </select>
            )}

            <div className="flex items-center gap-2">
              <Avatar photoUrl={currentUser.photoUrl} name={currentUser.name} size="sm" />
              <button
                onClick={onLogout}
                className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                title="Sign Out"
              >
                <span className="material-icons-round text-lg">logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 flex-1 space-y-6">
        {/* Toast Notification */}
        {actionSuccessMsg && (
          <div className="p-4 bg-emerald-600 text-white rounded-2xl shadow-lg flex items-center justify-between gap-3 animate-fade-in">
            <div className="flex items-center gap-2.5">
              <span className="material-icons-round text-xl">check_circle</span>
              <span className="text-sm font-semibold">{actionSuccessMsg}</span>
            </div>
            <button onClick={() => setActionSuccessMsg(null)} className="text-white/80 hover:text-white">
              <span className="material-icons-round text-sm">close</span>
            </button>
          </div>
        )}

        {/* Top Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold shadow-xs ${
              pendingOrgs.length > 0 ? 'bg-amber-100 text-amber-700 animate-pulse' : 'bg-slate-100 text-slate-600'
            }`}>
              <span className="material-icons-round">hourglass_top</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pending Review</p>
              <p className="text-2xl font-extrabold text-slate-900">{pendingOrgs.length}</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-xl font-bold shadow-xs">
              <span className="material-icons-round">school</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Active Institutions</p>
              <p className="text-2xl font-extrabold text-slate-900">{activeOrgs.length}</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center text-xl font-bold shadow-xs">
              <span className="material-icons-round">cast_for_education</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Teachers</p>
              <p className="text-2xl font-extrabold text-slate-900">{teacherUsers.length}</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center text-xl font-bold shadow-xs">
              <span className="material-icons-round">groups</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Parents &amp; Students</p>
              <p className="text-2xl font-extrabold text-slate-900">{parentUsers.length + students.length}</p>
            </div>
          </div>
        </div>

        {/* TAB 1: INSTITUTIONS & VERIFICATION QUEUE */}
        {activeTab === 'organizations' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            {/* Filter and Search Bar */}
            <div className="p-4 sm:p-5 border-b border-slate-200 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Status Filter Tabs */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
                <button
                  onClick={() => setStatusFilter('pending')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                    statusFilter === 'pending'
                      ? 'bg-amber-500 text-white shadow-xs'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span>Pending Review</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                    statusFilter === 'pending' ? 'bg-amber-700 text-white' : 'bg-slate-200 text-slate-800'
                  }`}>
                    {pendingOrgs.length}
                  </span>
                </button>

                <button
                  onClick={() => setStatusFilter('active')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                    statusFilter === 'active'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span>Active</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                    statusFilter === 'active' ? 'bg-emerald-800 text-white' : 'bg-slate-200 text-slate-800'
                  }`}>
                    {activeOrgs.length}
                  </span>
                </button>

                <button
                  onClick={() => setStatusFilter('suspended')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                    statusFilter === 'suspended'
                      ? 'bg-slate-700 text-white shadow-xs'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span>Suspended</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                    statusFilter === 'suspended' ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-800'
                  }`}>
                    {suspendedOrgs.length}
                  </span>
                </button>

                <button
                  onClick={() => setStatusFilter('rejected')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                    statusFilter === 'rejected'
                      ? 'bg-red-600 text-white shadow-xs'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span>Rejected</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                    statusFilter === 'rejected' ? 'bg-red-800 text-white' : 'bg-slate-200 text-slate-800'
                  }`}>
                    {rejectedOrgs.length}
                  </span>
                </button>

                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors whitespace-nowrap ${
                    statusFilter === 'all'
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  All ({organizations.length})
                </button>
              </div>

              {/* Search & Type Select */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:w-64">
                  <span className="material-icons-round absolute left-3 top-2.5 text-slate-400 text-sm">search</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search name, city, principal..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600">
                      <span className="material-icons-round text-xs">close</span>
                    </button>
                  )}
                </div>

                <select
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value as any)}
                  className="px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-700"
                >
                  <option value="all">All Types</option>
                  <option value="school">Schools</option>
                  <option value="coaching">Coaching</option>
                </select>
              </div>
            </div>

            {/* List Table / Cards */}
            {filteredOrgs.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
                  <span className="material-icons-round text-2xl">search_off</span>
                </div>
                <h4 className="text-sm font-bold text-slate-800 mb-1">No institutions found</h4>
                <p className="text-xs text-slate-500">No organizations match the current filter or search criteria.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredOrgs.map(org => {
                  const adminUser = users.find(u => u.id === org.adminId);
                  const orgStatus = org.status || 'pending';
                  const isOrgProcessing = isProcessing === org.id;

                  // External lookup links
                  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(`${org.name} ${org.address?.city || ''} ${org.address?.state || ''}`)}`;
                  const mapsUrl = org.verification?.publicUrl && org.verification.publicUrl.includes('map') 
                    ? org.verification.publicUrl 
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${org.name}, ${org.address?.city || ''}, ${org.address?.country || ''}`)}`;

                  return (
                    <div key={org.id} className="p-4 sm:p-5 hover:bg-slate-50/70 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Left Info */}
                        <div className="flex items-start gap-3.5 flex-1 min-w-0">
                          {org.photoUrl ? (
                            <img src={org.photoUrl} alt={org.name} className="w-12 h-12 rounded-2xl object-cover border border-slate-200 shadow-2xs flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-2xs">
                              {org.name.charAt(0).toUpperCase()}
                            </div>
                          )}

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="text-base font-bold text-slate-900 truncate">{org.name}</h3>
                              
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[10px] font-bold uppercase">
                                {org.type}
                              </span>

                              {/* Status Badge */}
                              {orgStatus === 'pending' && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 rounded-md text-[10px] font-extrabold uppercase flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                  Pending Verification
                                </span>
                              )}
                              {orgStatus === 'active' && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-md text-[10px] font-bold uppercase flex items-center gap-1">
                                  <span className="material-icons-round text-[11px]">check_circle</span>
                                  Active
                                </span>
                              )}
                              {orgStatus === 'suspended' && (
                                <span className="px-2 py-0.5 bg-slate-200 text-slate-800 rounded-md text-[10px] font-bold uppercase">
                                  Suspended
                                </span>
                              )}
                              {orgStatus === 'rejected' && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 rounded-md text-[10px] font-bold uppercase">
                                  Rejected
                                </span>
                              )}

                              {/* Revision Count Badge */}
                              {Boolean(org.verification?.isRevisedAfterSubmission || (org.verification?.revisionCount && org.verification.revisionCount > 1)) && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 border border-purple-200 rounded-md text-[10px] font-bold flex items-center gap-1" title="Information revised by principal">
                                  <span className="material-icons-round text-[11px]">history_edu</span>
                                  Rev #{org.verification?.revisionCount || 2}
                                </span>
                              )}
                            </div>

                            {/* Address & Contact Row */}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 mb-2">
                              <span className="flex items-center gap-1">
                                <span className="material-icons-round text-slate-400 text-xs">place</span>
                                {[org.address?.city, org.address?.state, org.address?.country].filter(Boolean).join(', ') || 'No location'}
                              </span>

                              {org.phone && (
                                <span className="flex items-center gap-1 font-mono text-slate-700">
                                  <span className="material-icons-round text-slate-400 text-xs">phone</span>
                                  {org.phone}
                                </span>
                              )}

                              {adminUser && (
                                <span className="flex items-center gap-1 text-slate-600">
                                  <span className="material-icons-round text-slate-400 text-xs">person</span>
                                  Principal: <strong className="text-slate-800">{adminUser.name}</strong> ({adminUser.email})
                                </span>
                              )}
                            </div>

                            {/* Verification Signals Pill Row */}
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                              {org.verification?.affiliationNumber ? (
                                <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-md font-mono font-semibold">
                                  ID: {org.verification.affiliationNumber}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-md">
                                  No Affiliation ID
                                </span>
                              )}

                              {org.verification?.publicUrl && (
                                <a
                                  href={org.verification.publicUrl.startsWith('http') ? org.verification.publicUrl : `https://${org.verification.publicUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-0.5 bg-blue-50 border border-blue-100 text-blue-700 hover:underline rounded-md font-medium flex items-center gap-1"
                                >
                                  <span className="material-icons-round text-xs">public</span>
                                  <span>Website/Maps</span>
                                </a>
                              )}

                              {org.verification?.documentUrl && (
                                <button
                                  onClick={() => setPreviewDocUrl(org.verification?.documentUrl || null)}
                                  className="px-2 py-0.5 bg-purple-50 border border-purple-100 text-purple-700 hover:bg-purple-100 rounded-md font-semibold flex items-center gap-1"
                                >
                                  <span className="material-icons-round text-xs">attachment</span>
                                  <span>View Proof Document</span>
                                </button>
                              )}

                              {/* 1-Click Verification Helpers */}
                              <a
                                href={searchUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md font-medium flex items-center gap-1"
                                title="Search Google for this School"
                              >
                                <span className="material-icons-round text-xs">travel_explore</span>
                                <span>Google Search</span>
                              </a>

                              <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md font-medium flex items-center gap-1"
                                title="Locate on Google Maps"
                              >
                                <span className="material-icons-round text-xs">map</span>
                                <span>Maps</span>
                              </a>

                              {org.phone && (
                                <a
                                  href={`https://wa.me/${org.phone.replace(/[^0-9]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-0.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-md font-medium flex items-center gap-1"
                                  title="Chat with Principal on WhatsApp"
                                >
                                  <span className="material-icons-round text-xs">chat</span>
                                  <span>WhatsApp</span>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Action Buttons */}
                        <div className="flex items-center gap-2 self-end lg:self-center flex-shrink-0">
                          <button
                            onClick={() => setConfiguringModulesOrg(org)}
                            className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1 border border-indigo-100"
                            title="Configure feature modules, parent policies, and AI API key"
                          >
                            <span className="material-icons-round text-sm">tune</span>
                            <span>Modules</span>
                            {(org.hasCustomApiKey || org.customApiKey) && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Custom AI Key Active" />}
                          </button>

                          <button
                            onClick={() => setSelectedOrgForDossier(org)}
                            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
                          >
                            <span className="material-icons-round text-sm">visibility</span>
                            <span>Details</span>
                          </button>

                          <button
                            onClick={() => setReassigningOrg(org)}
                            className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-xl text-xs font-bold transition-colors flex items-center gap-1 border border-amber-200"
                            title="Reassign Institution Administrator (Emergency / Board Governance)"
                          >
                            <span className="material-icons-round text-sm">manage_accounts</span>
                            <span>Admin</span>
                          </button>

                          {orgStatus === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(org)}
                                disabled={isOrgProcessing}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 shadow-xs disabled:opacity-50"
                              >
                                {isOrgProcessing ? (
                                  <span className="material-icons-round animate-spin text-sm">sync</span>
                                ) : (
                                  <span className="material-icons-round text-sm">check</span>
                                )}
                                <span>Approve</span>
                              </button>

                              <button
                                onClick={() => setRejectingOrg(org)}
                                disabled={isOrgProcessing}
                                className="px-3.5 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1 border border-red-200 disabled:opacity-50"
                              >
                                <span className="material-icons-round text-sm">close</span>
                                <span>Reject</span>
                              </button>
                            </>
                          )}

                          {orgStatus === 'active' && (
                            <button
                              onClick={() => handleSuspend(org)}
                              disabled={isOrgProcessing}
                              className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-xl text-xs font-bold transition-colors border border-amber-200 flex items-center gap-1"
                            >
                              <span className="material-icons-round text-sm">pause</span>
                              <span>Suspend</span>
                            </button>
                          )}

                          {(orgStatus === 'suspended' || orgStatus === 'rejected') && (
                            <button
                              onClick={() => handleReactivate(org)}
                              disabled={isOrgProcessing}
                              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
                            >
                              <span className="material-icons-round text-sm">restore</span>
                              <span>Reactivate</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: USER MANAGEMENT */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-900 text-sm">Platform Users Directory</h3>
                <span className="px-2 py-0.5 bg-slate-200 text-slate-800 rounded-full text-xs font-bold">
                  {filteredUsers.length}
                </span>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-60">
                  <span className="material-icons-round absolute left-3 top-2.5 text-slate-400 text-sm">search</span>
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={e => setUserSearchQuery(e.target.value)}
                    placeholder="Search by name, email..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                </div>

                <select
                  value={userRoleFilter}
                  onChange={e => setUserRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-700"
                >
                  <option value="all">All Roles</option>
                  <option value="super_admin">Super Admins</option>
                  <option value="principal">Principals</option>
                  <option value="teacher">Teachers</option>
                  <option value="parent">Parents</option>
                </select>
              </div>
            </div>

            <div className="divide-y divide-slate-100 max-h-[650px] overflow-y-auto">
              {filteredUsers.map(u => {
                const userOrg = organizations.find(o => o.id === u.orgId);
                const isSuper = u.role === 'super_admin';

                return (
                  <div key={u.id} className="p-4 flex items-center justify-between gap-4 hover:bg-slate-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar photoUrl={u.photoUrl} name={u.name} size="sm" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-slate-900 truncate">{u.name}</p>
                          <span className={`px-2 py-0.2 rounded-md text-[10px] font-bold uppercase ${
                            u.role === 'super_admin' ? 'bg-amber-100 text-amber-800' :
                            u.role === 'principal' ? 'bg-purple-100 text-purple-800' :
                            u.role === 'teacher' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {u.role}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                        {userOrg && (
                          <p className="text-[11px] text-slate-400 truncate">
                            Org: <strong className="text-slate-600">{userOrg.name}</strong>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {isSuper ? (
                        <button
                          onClick={() => {
                            if (confirm(`Remove Super Admin role from ${u.name}?`)) {
                              onUpdateUserRole(u.id, 'principal');
                            }
                          }}
                          className="px-2.5 py-1 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-200 font-semibold"
                        >
                          Revoke Admin
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            if (confirm(`Promote ${u.name} to Platform Super Admin?`)) {
                              onUpdateUserRole(u.id, 'super_admin');
                            }
                          }}
                          className="px-2.5 py-1 text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg font-semibold"
                        >
                          Make Super Admin
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* TAB 3: PLATFORM ANALYTICS */}
        {activeTab === 'analytics' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h4 className="text-sm font-bold text-slate-900 mb-4">Institutions Breakdown</h4>
              <div className="space-y-3 text-xs">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Pending Review</span>
                  <strong className="text-amber-600 font-bold">{pendingOrgs.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Active</span>
                  <strong className="text-emerald-600 font-bold">{activeOrgs.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Suspended</span>
                  <strong className="text-slate-700 font-bold">{suspendedOrgs.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Rejected</span>
                  <strong className="text-red-600 font-bold">{rejectedOrgs.length}</strong>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h4 className="text-sm font-bold text-slate-900 mb-4">User Roles Breakdown</h4>
              <div className="space-y-3 text-xs">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Super Admins</span>
                  <strong className="text-slate-800 font-bold">{superAdminUsers.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Principals</span>
                  <strong className="text-slate-800 font-bold">{principalUsers.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Teachers</span>
                  <strong className="text-slate-800 font-bold">{teacherUsers.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Parents</span>
                  <strong className="text-slate-800 font-bold">{parentUsers.length}</strong>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h4 className="text-sm font-bold text-slate-900 mb-4">Platform Scope</h4>
              <div className="space-y-3 text-xs">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Total Classrooms</span>
                  <strong className="text-indigo-600 font-bold">{classes.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Total Enrolled Students</span>
                  <strong className="text-indigo-600 font-bold">{students.length}</strong>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Spam Rejection Rate</span>
                  <strong className="text-slate-800 font-bold">
                    {organizations.length > 0 ? `${Math.round((rejectedOrgs.length / organizations.length) * 100)}%` : '0%'}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* REJECT MODAL */}
      {rejectingOrg && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-red-600">
                <span className="material-icons-round text-xl">block</span>
                <h3 className="font-bold text-slate-900 text-base">Reject Institution</h3>
              </div>
              <button onClick={() => setRejectingOrg(null)} className="text-slate-400 hover:text-slate-600">
                <span className="material-icons-round text-lg">close</span>
              </button>
            </div>

            <p className="text-xs text-slate-600 mb-4">
              Please specify the reason for rejecting <strong>{rejectingOrg.name}</strong>. The principal will be notified and can amend their details.
            </p>

            <div className="space-y-3 mb-4">
              <label className="text-xs font-bold text-slate-700 block">Select Primary Reason:</label>
              <select
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-xl text-xs bg-white text-slate-800"
              >
                <option value="Unverifiable institution details or incomplete credentials">
                  Unverifiable institution details or incomplete credentials
                </option>
                <option value="Invalid / Inactive contact phone number provided">
                  Invalid / Inactive contact phone number provided
                </option>
                <option value="Duplicate organization registration">
                  Duplicate organization registration
                </option>
                <option value="Suspicious or non-educational account">
                  Suspicious or non-educational account
                </option>
                <option value="Custom">Custom Explanation...</option>
              </select>

              {rejectionReason === 'Custom' && (
                <textarea
                  value={customRejectionText}
                  onChange={e => setCustomRejectionText(e.target.value)}
                  placeholder="Enter specific instructions for the principal..."
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs min-h-[80px]"
                />
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setRejectingOrg(null)}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReject}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs shadow-xs"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FULL DOSSIER DRAWER / MODAL */}
      {selectedOrgForDossier && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200 mb-5">
              <div className="flex items-center gap-3">
                {selectedOrgForDossier.photoUrl ? (
                  <img src={selectedOrgForDossier.photoUrl} alt="" className="w-12 h-12 rounded-2xl object-cover border border-slate-200" />
                ) : (
                  <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg">
                    {selectedOrgForDossier.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg leading-tight">{selectedOrgForDossier.name}</h3>
                  <p className="text-xs text-slate-500 capitalize">{selectedOrgForDossier.type} • ID: {selectedOrgForDossier.id}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrgForDossier(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center"
              >
                <span className="material-icons-round text-lg">close</span>
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 uppercase font-semibold text-[10px] block mb-1">Status</span>
                  <span className="font-bold text-slate-800 uppercase">{selectedOrgForDossier.status || 'pending'}</span>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 uppercase font-semibold text-[10px] block mb-1">Phone</span>
                  <span className="font-semibold text-slate-800">{selectedOrgForDossier.phone || 'None'}</span>
                </div>
              </div>

              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                <span className="text-slate-400 uppercase font-semibold text-[10px] block mb-1">Location Address</span>
                <span className="font-semibold text-slate-800">
                  {[
                    selectedOrgForDossier.address?.street,
                    selectedOrgForDossier.address?.city,
                    selectedOrgForDossier.address?.state,
                    selectedOrgForDossier.address?.country
                  ].filter(Boolean).join(', ') || 'None'}
                </span>
              </div>

              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                <span className="text-slate-400 uppercase font-semibold text-[10px] block mb-1">Affiliation / Board / Reg Number</span>
                <span className="font-mono font-bold text-slate-900 text-sm">
                  {selectedOrgForDossier.verification?.affiliationNumber || 'None provided'}
                </span>
              </div>

              {selectedOrgForDossier.verification?.publicUrl && (
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 uppercase font-semibold text-[10px] block mb-1">Website or Maps Link</span>
                  <a
                    href={selectedOrgForDossier.verification.publicUrl.startsWith('http') ? selectedOrgForDossier.verification.publicUrl : `https://${selectedOrgForDossier.verification.publicUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 underline font-semibold break-all hover:text-indigo-800"
                  >
                    {selectedOrgForDossier.verification.publicUrl}
                  </a>
                </div>
              )}

              {/* Revision & Submission Audit Info */}
              <div className="p-3.5 bg-indigo-50/70 rounded-xl border border-indigo-100">
                <span className="text-indigo-900 uppercase font-bold text-[10px] block mb-1">
                  Submission &amp; Revision Audit (Option B Auto-Reset)
                </span>
                <div className="grid grid-cols-2 gap-2 text-slate-700">
                  <div>
                    <span className="text-slate-400 text-[10px] block">Current Version:</span>
                    <span className="font-semibold text-slate-900">
                      Revision #{selectedOrgForDossier.verification?.revisionCount || 1}
                      {Boolean(selectedOrgForDossier.verification?.isRevisedAfterSubmission) && ' (Amended)'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Submitted At:</span>
                    <span className="font-semibold text-slate-900">
                      {selectedOrgForDossier.verification?.submittedAt ? (
                        (() => {
                          const d = (selectedOrgForDossier.verification?.submittedAt as any)?.toDate 
                            ? (selectedOrgForDossier.verification?.submittedAt as any).toDate() 
                            : new Date(selectedOrgForDossier.verification?.submittedAt as any);
                          return isNaN(d.getTime()) ? 'Recently' : d.toLocaleString();
                        })()
                      ) : (
                        'Initial Registration'
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {selectedOrgForDossier.verification?.documentUrl && (
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 uppercase font-semibold text-[10px] block mb-1">Attached Verification Document</span>
                  <button
                    onClick={() => setPreviewDocUrl(selectedOrgForDossier.verification?.documentUrl || null)}
                    className="mt-1 px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold rounded-lg hover:bg-indigo-100 flex items-center gap-1.5"
                  >
                    <span className="material-icons-round text-sm">file_present</span>
                    <span>Preview Document ({selectedOrgForDossier.verification.documentName || 'Attachment'})</span>
                  </button>
                </div>
              )}

              {/* Institution Leadership & Administration */}
              {(() => {
                const dossierAdminUser = users.find(u => u.id === selectedOrgForDossier.adminId);
                const vpCount = (selectedOrgForDossier.vicePrincipalEmails?.length || 0);
                return (
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 uppercase font-bold text-[10px]">
                        Institution Leadership &amp; Primary Ownership
                      </span>
                      <button
                        onClick={() => {
                          const o = selectedOrgForDossier;
                          setSelectedOrgForDossier(null);
                          setReassigningOrg(o);
                        }}
                        className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[11px] font-bold transition-colors flex items-center gap-1 shadow-2xs cursor-pointer"
                      >
                        <span className="material-icons-round text-xs">manage_accounts</span>
                        <span>Reassign Admin</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 bg-white p-3 rounded-xl border border-slate-200">
                      <div>
                        <span className="text-slate-400 text-[10px] uppercase font-bold block">Primary Principal / Admin</span>
                        <span className="font-bold text-slate-900 text-xs">{dossierAdminUser?.name || 'Administrator'}</span>
                        <span className="text-[11px] text-slate-500 font-mono block">{dossierAdminUser?.email || 'No email recorded'}</span>
                        {dossierAdminUser?.mobileNumber && (
                          <span className="text-[10px] text-slate-400 font-mono block">{dossierAdminUser.mobileNumber}</span>
                        )}
                      </div>
                      <div>
                        <span className="text-slate-400 text-[10px] uppercase font-bold block">Co-Admins / Vice Principals</span>
                        <span className="font-bold text-slate-900 text-xs">{vpCount} designated</span>
                        {selectedOrgForDossier.vicePrincipalEmails && selectedOrgForDossier.vicePrincipalEmails.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {selectedOrgForDossier.vicePrincipalEmails.slice(0, 3).map((vpe, i) => (
                              <span key={i} className="text-[10px] text-slate-500 font-mono block truncate">• {vpe}</span>
                            ))}
                            {selectedOrgForDossier.vicePrincipalEmails.length > 3 && (
                              <span className="text-[10px] text-slate-400 block">+ {selectedOrgForDossier.vicePrincipalEmails.length - 3} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Ownership & Reassignment Audit Trail */}
                    {selectedOrgForDossier.ownershipHistory && selectedOrgForDossier.ownershipHistory.length > 0 && (
                      <div className="pt-2 border-t border-slate-200/80">
                        <span className="text-slate-500 uppercase font-bold text-[10px] block mb-2">
                          Administrative Handover Ledger ({selectedOrgForDossier.ownershipHistory.length})
                        </span>
                        <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                          {selectedOrgForDossier.ownershipHistory.map((rec, idx) => (
                            <div key={idx} className="p-2.5 bg-white rounded-xl border border-slate-200 text-[11px] space-y-1">
                              <div className="flex items-center justify-between flex-wrap gap-1">
                                <span className={`px-1.5 py-0.2 rounded-md font-bold text-[9px] uppercase tracking-wide ${
                                  rec.transferType === 'super_admin_reassignment' 
                                    ? 'bg-amber-100 text-amber-800' 
                                    : 'bg-indigo-100 text-indigo-800'
                                }`}>
                                  {rec.transferType === 'super_admin_reassignment' ? 'Super Admin Recovery' : 'Self-Serve Handover'}
                                </span>
                                <span className="text-[10px] text-slate-400 font-mono">
                                  {new Date(rec.transferredAt).toLocaleString()}
                                </span>
                              </div>
                              <div className="text-slate-700">
                                <span className="text-slate-400">Previous: </span>
                                <span className="font-semibold">{rec.previousAdminName || rec.previousAdminEmail || rec.previousAdminId}</span>
                                <span className="mx-1 text-slate-300">→</span>
                                <span className="text-slate-400">New: </span>
                                <span className="font-bold text-slate-900">{rec.newAdminName || rec.newAdminEmail}</span>
                              </div>
                              {(rec.reason || rec.referenceTicket) && (
                                <p className="text-slate-500 text-[10px] italic">
                                  {rec.reason && `Reason: ${rec.reason}. `}
                                  {rec.referenceTicket && `Ref: ${rec.referenceTicket}`}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between gap-3 pt-6 border-t border-slate-100 mt-6">
              <button
                onClick={() => {
                  const org = selectedOrgForDossier;
                  setSelectedOrgForDossier(null);
                  setConfiguringModulesOrg(org);
                }}
                className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-xl text-xs flex items-center gap-1.5 border border-indigo-100"
              >
                <span className="material-icons-round text-sm">tune</span>
                <span>Configure Modules & AI</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedOrgForDossier(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs"
                >
                  Close
                </button>
                {(selectedOrgForDossier.status || 'pending') === 'pending' && (
                  <>
                    <button
                      onClick={() => setRejectingOrg(selectedOrgForDossier)}
                      className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-xl text-xs border border-red-200"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => handleApprove(selectedOrgForDossier)}
                      className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs shadow-xs"
                    >
                      Approve Institution
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODULES & FEATURE CONFIGURATION MODAL */}
      {configuringModulesOrg && (
        <OrgModulesModal
          org={configuringModulesOrg}
          isOpen={!!configuringModulesOrg}
          onClose={() => setConfiguringModulesOrg(null)}
          onSave={onUpdateOrg}
          mode="super_admin"
        />
      )}

      {/* DOCUMENT PREVIEW MODAL */}
      {previewDocUrl && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-3xl p-6 max-w-3xl w-full shadow-2xl border border-slate-100 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-indigo-600">verified</span>
                <h3 className="font-bold text-slate-900 text-sm">Verification Document</h3>
              </div>
              <button
                onClick={() => setPreviewDocUrl(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center"
              >
                <span className="material-icons-round text-lg">close</span>
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 rounded-2xl p-2 flex items-center justify-center min-h-[300px]">
              {previewDocUrl.startsWith('data:image') || previewDocUrl.includes('firebasestorage') ? (
                <img src={previewDocUrl} alt="Document" className="max-w-full max-h-[65vh] object-contain rounded-lg shadow-xs" />
              ) : (
                <iframe src={previewDocUrl} title="Document PDF" className="w-full h-[65vh] rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}
      {/* SUPER ADMIN REASSIGN MODAL */}
      {reassigningOrg && (
        <SuperAdminReassignModal
          isOpen={Boolean(reassigningOrg)}
          onClose={() => setReassigningOrg(null)}
          org={reassigningOrg}
          users={users}
          onReassignAdmin={handleConfirmReassignAdmin}
        />
      )}

      {/* SYSTEM QA & DIAGNOSTICS MODAL */}
      {showQAModal && (
        <SystemQADiagnosticsModal
          onClose={() => setShowQAModal(false)}
        />
      )}
    </div>
  );
};
